import { TFunction } from 'i18next';
import { LANG_TEXT } from '../constants/language';
import { ROLES } from '../consts/language';
import { IRestaurants, OrderItemStatus, Timestamp } from '../interfaces/index.types';
import { IFood, IGiftInfo, IMultiOrder, IOrders, OrderType } from '../pages/Dashboard/order.types';
import { IOrderHistory } from '../pages/OrderHistory/orderHistory.types';
import { formatSeatInfo } from './seatHelpers';

export const parseOrderList = async (
  allOrders: IOrders[],
  userInfo: any,
  currentSetting: any,
  seatDataMap: Map<string, any>,
  t: TFunction<'translation', undefined>,
  riderMap: Record<string, string>,
  enabledNewVersion: boolean
): Promise<IOrderHistory[]> => {
  const getRestaurantInfo = (id: string, field: 'name' | 'merchant_id' | 'location_id'): string => {
    if (userInfo.role !== ROLES.STAFF) {
      const found = userInfo.restaurants.find((r: IRestaurants) => r.id === id);
      return found ? found[field] : '';
    }
    return currentSetting?.[field] ?? '';
  };

  const getSeatNumber = (order_table_number: string): string => {
    const info = seatDataMap.get(order_table_number);
    return formatSeatInfo(info);
  };

  const calculateTax = (price: number, taxRate: number, quantity: number, isCancelled: boolean) => {
    if (isCancelled || quantity === 0) {
      return { beforeTax: 0, taxAmount: 0 };
    }
    if (taxRate > 0) {
      const beforeTax = Math.round(price / (1 + taxRate / 100));
      return { beforeTax, taxAmount: price - beforeTax };
    }
    return { beforeTax: price, taxAmount: 0 };
  };

  const buildFoodItem = (
    order: IFood,
    orderId: string,
    orderCreateTime: Timestamp,
    restaurantId: string,
    order_status: string,
    is_damaged: boolean | undefined,
    order_table_number: string,
    rider_id: string,
    rider_name: string,
    is_pre_order: boolean,
    order_type: OrderType,
    requested_delivery_time?: Timestamp,
    delivery_request?: string,
    multi_orders?: IMultiOrder[],
    completed_time?: Timestamp,
    email_or_phone?: string,
    email?: string,
    phone?: string,
    gift_info?: IGiftInfo
  ): IOrderHistory => {
    const actualRestaurantId = order.restaurantId ?? restaurantId;
    const isCancelled = order_status === OrderItemStatus.CANCELLED;
    const quantity = order.item_deleted_quantity !== undefined ? order.item_quantity - order.item_deleted_quantity : order.item_quantity;
    const taxRate = parseInt(order.tax_rate ?? '0', 10);
    const itemPrice = isCancelled || quantity === 0 ? 0 : order.item_price;
    const itemQuantity = isCancelled ? 0 : quantity;

    const { beforeTax: beforeTaxPerItem, taxAmount: taxAmountPerItem } = calculateTax(order.item_price, taxRate, itemQuantity, isCancelled);

    return {
      id: orderId,
      seat_number: getSeatNumber(order_table_number),
      rider_id,
      rider_name,
      item_food_id: order.item_food_id,
      sku: order.sku,
      item_name: order.item_name,
      item_customize_settings: order.item_customize_settings,
      damaged_status: is_damaged ? 'Y' : 'N',
      item_price: itemPrice,
      item_quantity: itemQuantity,
      ttl_price: itemPrice * itemQuantity,
      tax_rate: taxRate.toString(),
      tax_amount: isCancelled ? 0 : taxAmountPerItem,
      before_tax_amount: isCancelled || quantity === 0 ? 0 : beforeTaxPerItem,
      order_create_time: orderCreateTime,
      restaurant_id: actualRestaurantId,
      restaurant_name: getRestaurantInfo(actualRestaurantId, 'name'),
      merchant_id: getRestaurantInfo(actualRestaurantId, 'merchant_id'),
      location_id: getRestaurantInfo(actualRestaurantId, 'location_id'),
      type: 'food',
      order_status,
      is_pre_order,
      order_type,
      requested_delivery_time,
      delivery_request,
      multi_orders,
      completed_time,
      email_or_phone: email_or_phone || '',
      email: email || email_or_phone || '',
      phone: phone || '',
      gift_info: gift_info || {},
    };
  };

  const buildFeeItem = (
    orderId: string,
    order_create_time: Timestamp,
    restaurant_id: string,
    order_status: string,
    is_damaged: boolean | undefined,
    order_table_number: string,
    rider_id: string,
    rider_name: string,
    is_pre_order: boolean,
    order_type: OrderType,
    requested_delivery_time?: Timestamp,
    delivery_request?: string,
    multi_orders?: IMultiOrder[],
    completed_time?: Timestamp,
    email_or_phone?: string,
    email?: string,
    phone?: string,
    gift_info?: IGiftInfo,
    restaurantIdFromFood?: string,
    enabledNewVersion?: boolean
  ): IOrderHistory => {
    const actualRestaurantId = restaurantIdFromFood ?? restaurant_id;
    const isDamaged = order_status === OrderItemStatus.CANCELLED || is_damaged;
    const itemPrice = isDamaged ? 0 : 770;
    const itemQuantity = isDamaged ? 0 : 1;
    const { beforeTax: beforeTaxAmount, taxAmount } = calculateTax(770, 10, itemQuantity, !!isDamaged);

    return {
      id: orderId,
      seat_number: getSeatNumber(order_table_number),
      rider_id,
      rider_name,
      item_food_id: t(LANG_TEXT.DELIVERY_FEE_ETC),
      sku: '',
      item_name: t(LANG_TEXT.DELIVERY_FEE),
      item_customize_settings: [],
      item_price: itemPrice,
      item_quantity: itemQuantity,
      ttl_price: itemPrice * itemQuantity,
      damaged_status: is_damaged ? 'Y' : 'N',
      tax_rate: '10',
      tax_amount: taxAmount,
      before_tax_amount: beforeTaxAmount,
      order_create_time,
      restaurant_id: enabledNewVersion ? 'N/A' : actualRestaurantId,
      restaurant_name: enabledNewVersion ? 'N/A' : getRestaurantInfo(actualRestaurantId, 'name'),
      merchant_id: enabledNewVersion ? 'N/A' : getRestaurantInfo(actualRestaurantId, 'merchant_id'),
      location_id: enabledNewVersion ? 'N/A' : getRestaurantInfo(actualRestaurantId, 'location_id'),
      type: 'fee',
      order_status,
      is_pre_order,
      order_type,
      requested_delivery_time,
      delivery_request,
      multi_orders,
      completed_time,
      email_or_phone: email_or_phone || '',
      email: email || email_or_phone || '',
      phone: phone || '',
      gift_info: gift_info || {},
    };
  };

  const newData: IOrderHistory[] = [];

  allOrders.forEach((order: IOrders) => {
    const {
      id,
      order_create_time,
      restaurant_id,
      type,
      promotion_foods,
      foods,
      order_status,
      is_damaged,
      order_table_number,
      rider_id = '',
      is_pre_order,
      order_type,
      requested_delivery_time,
      delivery_request,
      multi_orders,
      completed_time,
      email_or_phone,
      email,
      phone,
      gift_info = {},
    } = order;

    // For multi-orders: rider is assigned per restaurant in multi_orders[].rider_id.
    // Resolve rider_id per food item by matching restaurantId → multi_orders entry.
    const getRiderIdForFood = (food: IFood): string => {
      if (multi_orders && multi_orders.length > 0) {
        const foodRestaurantId = food.restaurantId ?? restaurant_id;
        const segment = multi_orders.find((m) => m.restaurant_id === foodRestaurantId);
        if (segment?.rider_id) return segment.rider_id;
      }
      return rider_id;
    };

    const rider_name = riderMap[rider_id] ?? '';

    const foodList = type === 'promotion' && promotion_foods ? promotion_foods : foods;

    foodList.forEach((food: IFood) => {
      const foodRiderId = getRiderIdForFood(food);
      const foodRiderName = riderMap[foodRiderId] ?? rider_name;
      newData.push(
        buildFoodItem(
          food,
          id,
          order_create_time,
          restaurant_id,
          order_status,
          is_damaged,
          order_table_number,
          foodRiderId,
          foodRiderName,
          is_pre_order,
          order_type,
          requested_delivery_time,
          delivery_request,
          multi_orders,
          completed_time,
          email_or_phone,
          email,
          phone,
          gift_info
        )
      );
    });

    let restaurantIdForFee: string | undefined = undefined;
    if (multi_orders && multi_orders.length > 0) {
      const multiIds = Array.from(new Set(multi_orders.map((m) => m.restaurant_id).filter(Boolean)));
      if (multiIds.length === 1) restaurantIdForFee = multiIds[0];
    } else {
      const foodRestaurantIds = Array.from(new Set(foodList.map((f: IFood) => f.restaurantId).filter(Boolean)));
      if (foodRestaurantIds.length === 1) restaurantIdForFee = foodRestaurantIds[0];
    }

    newData.push(
      buildFeeItem(
        id,
        order_create_time,
        restaurant_id,
        order_status,
        is_damaged,
        order_table_number,
        rider_id,
        rider_name,
        is_pre_order,
        order_type,
        requested_delivery_time,
        delivery_request,
        multi_orders,
        completed_time,
        email_or_phone,
        email,
        phone,
        gift_info,
        restaurantIdForFee,
        enabledNewVersion
      )
    );
  });

  return newData;
};
